import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation27() {
  const { lesson, course, accent } = findLesson("piano-foundation", "27");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Three minor scales: <strong>Natural</strong> (♭3 ♭6 ♭7), <strong>Harmonic</strong> (♭3 ♭6, natural 7), <strong>Melodic</strong> (jazz: just ♭3; classical: different ascent and descent).</Highlight>
        <Highlight>Plus a fourth — <strong>Dorian</strong> — which is ♭3 and ♭7 but a <em>natural</em> 6. The "beautiful" one.</Highlight>
        <Highlight>To "exoticise" any scale, <strong>flat the 2</strong> (gives Phrygian / Spanish flavour) or <strong>sharp the 4</strong> (gives a Hungarian / mysterious flavour). Or do both.</Highlight>
        <Highlight>Same fingering rules as Lessons 25-26: <strong>3+5</strong> for most white scales, <strong>4+4</strong> when the thumb would land on a black key.</Highlight>
        <Highlight>Every minor scale shares its key signature with a <strong>major scale a minor third up</strong>. C minor and E♭ major use the exact same notes — they're called <em>relative</em> to each other.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The minor scale family" title="One root, three (or four) flavours" accent={accent.hex}>
        <p>
          From any major scale, three different minor scales fall out depending on which
          notes you flatten:
        </p>

        <Vocab>
          <Term name="Natural minor" def="flatten the 3rd, 6th, and 7th. C natural minor: C-D-E♭-F-G-A♭-B♭-C." />
          <Term name="Harmonic minor" def="like natural minor, but the 7th comes back to natural (raised 7). Has a distinctive 'augmented second' gap between 6 and 7." />
          <Term name="Melodic minor (jazz)" def="major scale with just the 3rd flattened. Same notes ascending and descending." />
          <Term name="Melodic minor (classical)" def="ascending uses major 6 + major 7; descending uses ♭6 + ♭7 (becomes natural minor)." />
          <Term name="Dorian" def="♭3 and ♭7, but the 6th stays natural. C Dorian: C-D-E♭-F-G-A-B♭-C." />
        </Vocab>
      </Insight>

      <Insight eyebrow="Each minor as a delta from major" title="What changes — and what stays the same" accent={accent.hex}>
        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[13px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Scale (in C)</th>
                <th>1</th><th>2</th><th>3</th><th>4</th><th>5</th><th>6</th><th>7</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-nsm-border"><td>Major</td><td>C</td><td>D</td><td>E</td><td>F</td><td>G</td><td>A</td><td>B</td></tr>
              <tr className="border-t border-nsm-border"><td>Natural minor</td><td>C</td><td>D</td><td className="text-rose-300">E♭</td><td>F</td><td>G</td><td className="text-rose-300">A♭</td><td className="text-rose-300">B♭</td></tr>
              <tr className="border-t border-nsm-border"><td>Harmonic minor</td><td>C</td><td>D</td><td className="text-rose-300">E♭</td><td>F</td><td>G</td><td className="text-rose-300">A♭</td><td>B</td></tr>
              <tr className="border-t border-nsm-border"><td>Melodic (jazz)</td><td>C</td><td>D</td><td className="text-rose-300">E♭</td><td>F</td><td>G</td><td>A</td><td>B</td></tr>
              <tr className="border-t border-nsm-border"><td>Dorian</td><td>C</td><td>D</td><td className="text-rose-300">E♭</td><td>F</td><td>G</td><td>A</td><td className="text-rose-300">B♭</td></tr>
            </tbody>
          </table>
        </div>

        <KeyIdea>
          <strong>1, 4, and 5 never change</strong> across any of the minors. Only the 3rd,
          6th, and 7th shift. That's why the same fingering still works — the hand position
          is identical to the major.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The relative-minor relationship" title="C minor = E♭ major, written from a different starting note" accent={accent.hex}>
        <Pull>
          A simple formula would be what is the sixth note of E♭? The answer is C. So
          that will be the relative minor. Okay. The other thing you could ask is what
          is the minor third of C? The answer is E♭. So you could say E♭ is the relative
          major of C minor, or C minor is the relative minor of E♭ major, depending on
          where you're coming from.
        </Pull>

        <p>
          Every minor scale "shares" its key signature with a major scale up a minor third.
          A minor = C major. D minor = F major. E minor = G major. Once you know the major
          fingering, the relative minor is essentially free — same notes, different starting
          point.
        </p>
      </Insight>

      <Insight eyebrow="The Dorian" title="The 'beautiful' minor" accent={accent.hex}>
        <p>
          Dorian is the only minor that keeps the 6th natural. That major-6th gives it a
          brighter feel than natural minor while still being unambiguously minor. You hear
          Dorian behind countless film scores, jazz pieces, and folk modal songs.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          C Dorian: C - D - E♭ - F - G - A - B♭ - C
        </p>

        <Pull>
          Beautiful scale, the Dorian.
        </Pull>
      </Insight>

      <Insight eyebrow="Exoticising any scale" title="Flat the 2, or sharp the 4" accent={accent.hex}>
        <p>
          To take any scale into "exotic" territory, change one of two notes:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>Flat the 2</strong> → gives a Phrygian or Spanish/flamenco feel.
            C: C - <strong>D♭</strong> - E♭ - F - G - A♭ - B♭.
          </li>
          <li>
            <strong>Sharp the 4</strong> → gives a Hungarian/mysterious feel.
            C: C - D - E♭ - <strong>F♯</strong> - G - A♭ - B♭.
          </li>
          <li>
            <strong>Both at once</strong> — even more exotic. Flamenco-Hungarian-spy-thriller.
          </li>
        </ul>

        <Tip>
          When flattening the 2, you might land your thumb on a black note. If that's awkward
          (as in F major from Lesson 25), use the <strong>4 + 4 fingering</strong> instead
          of 3 + 5. Same crossing logic — just one finger later.
        </Tip>
      </Insight>

      <Insight eyebrow="The fingering still works" title="Same crossing rule across every minor" accent={accent.hex}>
        <p>
          The fingering for major scales transfers exactly to minor:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>White minor scales</strong> — 1-2-3 / cross / 1-2-3-4-5. Same as major.</li>
          <li><strong>Black minor scales</strong> — cross the thumb whenever a white note follows a black note.</li>
          <li><strong>F minor and other "thumb-on-black-key" exceptions</strong> — switch to 4 + 4 fingering, like F major.</li>
        </ul>

        <KeyIdea>
          The fingering principles you know already cover <em>every scale</em> — major,
          minor, modal, exotic. Once those rules are in your hand, you can play any seven-note
          scale on the piano without learning new fingerings.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Practice path" title="One root, four minors, one black scale" accent={accent.hex}>
        <Steps>
          <Step>Pick a root — start with C. Write all four minor scales (natural, harmonic, melodic-jazz, Dorian) in worms.</Step>
          <Step>Play C natural minor ascending and descending. LH on the C pulse.</Step>
          <Step>Switch to harmonic minor — only the 7th moves. Watch the augmented-second gap between A♭ and B.</Step>
          <Step>Switch to melodic minor (jazz). Just the 3rd is flat — feels almost like major.</Step>
          <Step>Switch to Dorian. Notice the brighter 6th compared to natural minor.</Step>
          <Step>Move to D minor. Then E minor. Eventually try a black-key minor — E♭ minor or similar.</Step>
          <Step>Once each is comfortable, try exoticising — flat the 2, or sharp the 4. Hear how the same scale shifts colour.</Step>
        </Steps>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-28"
        label="Lesson 28 · Left Hand Scales Ascending & Descending"
        why="Three lessons of right-hand scales, and the left hand has been waiting. The next lesson teaches scales on the LH using the same crossing principles in mirror image."
      />
    </LessonShell>
  );
}
